import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export function PrincipalAdminLink({ tenantId, onBusyChange, onLinked }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [selection, setSelection] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState("");
  const running = useRef(false);
  const attempt = useRef(null);
  const mounted = useRef(true);
  const endpoint = `/platform/tenants/${tenantId}`;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const load = async () => {
    setData(null); setSelection(""); setConfirmed(false); setError("");
    try {
      const result = await api.get(endpoint + "/principal-admin-candidates");
      if (mounted.current) setData(result.data);
    } catch (e) { if (mounted.current) setError(apiErr(e)); }
  };
  const show = () => { setOpen(true); setUncertain(false); attempt.current = null; load(); };
  const close = () => {
    if (running.current) return;
    setOpen(false); setSelection(""); setConfirmed(false); setUncertain(false);
    attempt.current = null;
  };
  const selected = data?.candidates?.find((u) => u.id === selection);
  const submit = async (event) => {
    event.preventDefault();
    if (running.current || !selected || !confirmed || !data?.can_link) return;
    if (!attempt.current) {
      const bytes = new Uint8Array(16); window.crypto.getRandomValues(bytes);
      attempt.current = {
        key: Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(""),
        payload: { user_id: selected.id, confirm_user_id: selected.id, confirm_tenant_id: tenantId },
      };
    }
    const current = attempt.current;
    running.current = true; setBusy(true); onBusyChange(true); setError("");
    let linked = false;
    try {
      const result = await api.post(endpoint + "/principal-admin-link", current.payload,
        { headers: { "Idempotency-Key": current.key } });
      linked = true;
      if (mounted.current) {
        attempt.current = null; setOpen(false); setSelection(""); setConfirmed(false);
        if (result.data?.audit_pending) toast.warning("Administrador vinculado. Auditoria pendente em recuperação automática.");
        else toast.success("Administrador principal vinculado por confirmação explícita.");
      }
    } catch (e) {
      if (mounted.current) {
        if ([400, 403, 404, 409, 422].includes(e?.response?.status)) {
          attempt.current = null; setSelection(""); setConfirmed(false); setUncertain(false);
          setData(null); setError(apiErr(e));
        } else {
          setUncertain(true);
          setError("Resultado ainda não confirmado. Repita com a mesma seleção; o vínculo não será aplicado duas vezes.");
        }
      }
    } finally {
      running.current = false; onBusyChange(false);
      if (mounted.current) { setBusy(false); if (linked) onLinked(); }
    }
  };
  return (
    <div className="space-y-2">
      <Button type="button" variant="secondary" data-testid="principal-link-open" onClick={show}>
        Vincular administrador existente
      </Button>
      <Dialog open={open} onOpenChange={(value) => { if (!value) close(); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle>Confirmar administrador principal</DialogTitle>
            <DialogDescription>
              Selecione uma conta existente desta empresa. A confirmação registra o responsável principal agora; não identifica quem criou a empresa no passado. Nenhuma senha ou permissão será alterada.
            </DialogDescription>
          </DialogHeader>
          {!data && !error && <p role="status">Carregando administradores elegíveis...</p>}
          {data && <p>Empresa: <strong>{data.tenant.name}</strong></p>}
          {data?.can_link === false && <p role="status">{data.reason}</p>}
          {data?.can_link && data.candidates.length === 0 && <p role="status" data-testid="principal-link-empty">{data.empty_reason}</p>}
          {data?.can_link && data.candidates.length > 0 && <form onSubmit={submit} data-testid="principal-link-form" className="space-y-4">
            <label htmlFor="principal-link-selection">Administrador existente</label>
            <select id="principal-link-selection" data-testid="principal-link-selection"
              value={selection} disabled={busy || uncertain} className="w-full bg-slate-950 rounded p-2"
              onChange={(e) => { setSelection(e.target.value); setConfirmed(false); }}>
              <option value="">Selecione uma pessoa</option>
              {data.candidates.map((u) => <option key={u.id} value={u.id}>{u.name || u.email} — {u.email} — Ativo / Administrador</option>)}
            </select>
            {selected && <div data-testid="principal-link-confirmation">
              <p>Vincular <strong>{selected.name || selected.email}</strong> ({selected.email}) à empresa <strong>{data.tenant.name}</strong>.</p>
              <p className="text-xs text-slate-400">ID da conta: {selected.id}</p>
              <label className="flex gap-2 mt-2">
                <input type="checkbox" data-testid="principal-link-confirm" checked={confirmed} disabled={busy || uncertain}
                  onChange={(e) => setConfirmed(e.target.checked)} />
                Confirmo esta pessoa como administrador principal desta empresa.
              </label>
            </div>}
            <Button type="submit" data-testid="principal-link-submit" disabled={busy || !selected || !confirmed}>
              {busy ? "Vinculando..." : uncertain ? "Repetir confirmação" : "Confirmar vínculo"}
            </Button>
          </form>}
          {error && <p role="alert">{error}</p>}
          {!data && error && <Button type="button" onClick={load}>Atualizar lista</Button>}
          <DialogFooter><Button type="button" variant="ghost" disabled={busy} onClick={close}>Cancelar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
